import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';

import { format } from '@breezeway/brzw-react-core';
import AssignedUsers from '../../../components/assigned_users';
import RenderWrapper from './render_wrapper';
import { addFlyout, FLYOUT_TYPES } from '../../../utils/flyout';
import { DEPARTMENTS } from '../../../utils/departments';
import { personName } from '../../../utils/people_helpers';
import { SCHEDULE_SUBJECT, SINGLE_TASK_LIFE_STATUS, singleTaskLifeStatuses } from '../../constants';
import { TaskTooltip } from '../../../components/task_tooltip';

import './single_task.scss';

const { OVERDUE, UNASSIGNED, ASSIGNED } = SINGLE_TASK_LIFE_STATUS;

class SingleTask extends Component {
  constructor(props) {
    super(props);

    this.CLASS_NAME = 'brzw-calendar-single-task';

    this.state = {
      isOpen: false,
    };

    this.colorStatus = this.colorStatus.bind(this);
    this.getBackgroundModifier = this.getBackgroundModifier.bind(this);
    this.mappedAssignments = this.mappedAssignments.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onTaskClick = this.onTaskClick.bind(this);
    this.renderAssignedUserItem = this.renderAssignedUserItem.bind(this);
    this.renderAssignedUsers = this.renderAssignedUsers.bind(this);
    this.renderBadgeNumber = this.renderBadgeNumber.bind(this);
    this.renderCard = this.renderCard.bind(this);
    this.renderCardContent = this.renderCardContent.bind(this);
    this.renderClickableCard = this.renderClickableCard.bind(this);
    this.renderTaskTiming = this.renderTaskTiming.bind(this);

    if (props.withToolTip) {
      this.taskRef = React.createRef();
    }
  }

  onTaskClick() {
    const { onTaskClick, taskDraftId, taskId } = this.props;

    if (!_.isNull(taskId) || !_.isNull(taskDraftId)) {
      addFlyout(FLYOUT_TYPES.TASK_EDIT, { taskId: taskId || taskDraftId });
    } else if (_.isFunction(onTaskClick)) {
      onTaskClick();
    }
  }

  onFocus() {
    if (this.props.withToolTip) {
      this.setState({
        isOpen: true,
      });
    }
  }

  onBlur() {
    if (this.props.withToolTip) {
      this.setState({
        isOpen: false,
      });
    }
  }

  getBackgroundModifier() {
    const { isDraft, isPaused, isStarted, lifeStatus } = this.props;

    const isOverdue = lifeStatus === OVERDUE;
    const isNotInProgress = !isPaused && !isStarted;
    let modifier = '';

    if ((isNotInProgress || isOverdue) && _.includes(singleTaskLifeStatuses, lifeStatus)) {
      modifier = isDraft
        ? `${this.CLASS_NAME}--draft ${this.CLASS_NAME}--${lifeStatus}-draft`
        : `${this.CLASS_NAME}--${lifeStatus}`;
    }

    return modifier;
  }

  mappedAssignments() {
    const { assignedPeople, people } = this.props;
    return _.map(assignedPeople, person => ({
      ..._.omit(person, 'company_people_id'),
      company_person: _.find(people, { id: _.get(person, 'company_people_id') }),
    }));
  }

  colorStatus() {
    const { lifeStatus, taskStatus } = this.props;
    const isNew = taskStatus.code === 'created' && taskStatus.stage === 'new';
    const isUnassigned = lifeStatus === UNASSIGNED;
    const isAssigned = lifeStatus === ASSIGNED;

    let status = lifeStatus;

    if (isNew && isUnassigned) {
      status = 'is-new-unassigned';
    }

    if (isNew && isAssigned) {
      status = 'is-new-assigned';
    }

    return status;
  }

  renderBadgeNumber() {
    const { badgeNumber } = this.props;

    return <div className={`${this.CLASS_NAME}__badge-number`}>{badgeNumber}</div>;
  }

  renderAssignedUserItem(key, userName, assignmentStatus = null) {
    const { empty } = this.props;
    const itemClassName = classnames(`${this.CLASS_NAME}__assigned-user`, {
      [`${this.CLASS_NAME}__assigned-user--with-status`]: assignmentStatus,
    });
    return (
      <li className={itemClassName} key={key}>
        {!empty && (
          <Fragment>
            {assignmentStatus && (
              <span
                className={classnames(
                  `${this.CLASS_NAME}__assignment-status`,
                  `${this.CLASS_NAME}__assignment-status--${assignmentStatus}`,
                )}
              />
            )}
            <span className={`${this.CLASS_NAME}__assigned-user-name hide-overflow`}>
              {userName}
            </span>
          </Fragment>
        )}
      </li>
    );
  }

  renderAssignedUsers() {
    const { assignedPeople } = this.props;
    const mappedUsers = _.map(this.mappedAssignments(), assignment => {
      const personId = _.get(assignment, 'company_person.id');
      const assignmentStatus = _.get(assignment, 'type_task_user_status.code');
      return this.renderAssignedUserItem(
        `assigned-user-${personId}`,
        personName(assignment.company_person),
        assignmentStatus,
      );
    });
    return (
      <ul className={`${this.CLASS_NAME}__assigned-users list`}>
        {_.size(assignedPeople)
          ? mappedUsers
          : this.renderAssignedUserItem('assigned-user-empty', 'Unassigned')}
      </ul>
    );
  }

  renderCardContent() {
    const {
      departmentCode,
      empty,
      expanded,
      scheduleType,
      taskCompany,
      taskName,
      taskSeries,
    } = this.props;

    return (
      <Fragment>
        <div className={`${this.CLASS_NAME}__inner-wrapper layout-box l__has-columns`}>
          <div className={`${this.CLASS_NAME}__sidebar`}>
            {!empty && (
              <Fragment>
                <span className={`${this.CLASS_NAME}__icon icon-${departmentCode}`} />

                {taskSeries && expanded && (
                  <span className={`${this.CLASS_NAME}__icon icon-refresh`} />
                )}
              </Fragment>
            )}
          </div>

          {expanded && (
            <div className={`${this.CLASS_NAME}__content layout-box l__has-rows l__flexes-to-1`}>
              <span className={`${this.CLASS_NAME}__task-name hide-overflow`}>
                {!empty && _.truncate(taskName, { length: 30 })}
              </span>
              {scheduleType === SCHEDULE_SUBJECT.PROPERTY && this.renderAssignedUsers()}
              {!empty && scheduleType === SCHEDULE_SUBJECT.USER && (
                <AssignedUsers
                  assignedPeople={this.mappedAssignments()}
                  taskCompany={taskCompany}
                />
              )}
            </div>
          )}
        </div>

        {expanded && this.renderTaskTiming()}
      </Fragment>
    );
  }

  renderClickableCard() {
    const { leftOffset, withText, lengthMultiplier, rightOffset } = this.props;
    const onClickMoreButton = withText ? null : this.onTaskClick;

    return (
      <button
        className={`${this.CLASS_NAME}__wrapper`}
        data-left-offset={leftOffset}
        data-length-multiplier={lengthMultiplier}
        data-right-offset={leftOffset + rightOffset}
        onClick={onClickMoreButton}
        onMouseEnter={this.onFocus}
        onMouseLeave={this.onBlur}
        type="button"
      >
        {this.renderCardContent()}
      </button>
    );
  }

  renderCard() {
    const { leftOffset, lengthMultiplier, rightOffset } = this.props;

    return (
      <div
        className={`${this.CLASS_NAME}__wrapper`}
        data-left-offset={leftOffset}
        data-length-multiplier={lengthMultiplier}
        data-right-offset={leftOffset + rightOffset}
      >
        {this.renderCardContent()}
      </div>
    );
  }

  renderTaskInfo() {
    const { currentCompany, taskCompany, taskHome } = this.props;
    const currentCompanyId = _.get(currentCompany, 'id');
    const taskCompanyId = _.get(taskCompany, 'id');
    const isTaskOwner = _.isEqual(taskCompanyId, currentCompanyId);

    const propertyName = _.get(taskHome, 'display', 'Unnamed');
    const assigningCompanyName = _.get(taskCompany, 'name', '');

    return (
      <Fragment>
        {!isTaskOwner && (
          <span className={`${this.CLASS_NAME}__address ${this.CLASS_NAME}__address--bigger`}>
            {assigningCompanyName}
          </span>
        )}
        <p
          className={classnames(`${this.CLASS_NAME}__address`, {
            [`${this.CLASS_NAME}__address--bigger`]: isTaskOwner,
          })}
        >
          {_.truncate(propertyName, { length: 26 })}
        </p>
      </Fragment>
    );
  }

  renderTaskTiming() {
    const { isPaused, isStatusCompleted, scheduleType, showTiming, totalTime } = this.props;

    const timeString = format.datetime.stringToHoursMinutes(totalTime);
    const iconClassName = classnames(`${this.CLASS_NAME}__task-timing-container`, {
      [`${this.CLASS_NAME}__task-timing--no-icon`]: isStatusCompleted,
      'icon-pause': isPaused && !isStatusCompleted,
      'icon-start': !isPaused && !isStatusCompleted,
    });
    const scheduleTypeIsUser = scheduleType === SCHEDULE_SUBJECT.USER;

    return (
      <div className={`${this.CLASS_NAME}__task-timing`}>
        {scheduleTypeIsUser && this.renderTaskInfo()}
        {showTiming && <p className={iconClassName}>{timeString}</p>}
      </div>
    );
  }

  render() {
    const {
      badgeNumber,
      clickable,
      containerClassName: propsContainerClassName,
      displayTooltipInPortal,
      empty,
      expanded,
      firstInGroup,
      flyoutInformations,
      isInGroup,
      isPaused,
      isStarted,
      lastInGroup,
      readyStatus,
      scheduleType,
      showTiming,
      taskCommentCount,
      taskId,
      taskIssueCount,
      taskName,
      taskStatus,
      tooltipSettings,
      withText,
      withToolTip,
    } = this.props;
    const isNew = taskStatus.code === 'created' && taskStatus.stage === 'new';
    const scheduleHasTaskTiming = scheduleType === SCHEDULE_SUBJECT.USER || showTiming;
    const renderTaskModal = clickable && !empty && withText ? this.onTaskClick : null;
    const containerClassName = classnames(
      this.CLASS_NAME,
      propsContainerClassName,
      this.getBackgroundModifier(),
      {
        [`${this.CLASS_NAME}--clickable`]: clickable,
        [`${this.CLASS_NAME}--curled`]: !expanded,
        [`${this.CLASS_NAME}--empty`]: empty,
        [`${this.CLASS_NAME}--first-in-group`]: firstInGroup,
        [`${this.CLASS_NAME}--in-group`]: isInGroup,
        [`${this.CLASS_NAME}--is-paused`]: isPaused,
        [`${this.CLASS_NAME}--is-started`]: isStarted,
        [`${this.CLASS_NAME}--is-new`]: isNew,
        [`${this.CLASS_NAME}--last-in-group`]: lastInGroup,
        [`${this.CLASS_NAME}--with-badge-number`]: badgeNumber,
        [`${this.CLASS_NAME}--with-schedule-timing`]: scheduleHasTaskTiming,
        [`${this.CLASS_NAME}--with-text`]: withText,
        [`${this.CLASS_NAME}--with-timing`]: showTiming && !isPaused,
      },
    );

    const taskComponent = {
      color: this.colorStatus(),
      comments: taskCommentCount,
      issues: taskIssueCount,
      name: taskName,
      ready_status: readyStatus,
      task_status: taskStatus,
      taskId,
    };

    const taskContent = (
      <div className={containerClassName} ref={this.taskRef}>
        {clickable && !empty ? this.renderClickableCard() : this.renderCard()}
        {badgeNumber && this.renderBadgeNumber()}
      </div>
    );

    return (
      <RenderWrapper
        flyoutInformations={flyoutInformations}
        onTaskClick={renderTaskModal}
        taskId={taskId}
        withText={withText}
      >
        {withToolTip ? (
          <TaskTooltip
            displayTooltipInPortal={displayTooltipInPortal}
            isOpen={this.state.isOpen}
            taskComponent={taskComponent}
            taskRef={this.taskRef}
            {...tooltipSettings}
          >
            {taskContent}
          </TaskTooltip>
        ) : (
          taskContent
        )}

        {withText && (
          <span className={`${this.CLASS_NAME}__text`}>{_.truncate(taskName, { length: 30 })}</span>
        )}
      </RenderWrapper>
    );
  }
}

SingleTask.propTypes = {
  assignedPeople: PropTypes.arrayOf(PropTypes.shape({})),
  badgeNumber: PropTypes.number,
  clickable: PropTypes.bool,
  containerClassName: PropTypes.string,
  containerWidth: PropTypes.number,
  currentCompany: PropTypes.shape({}),
  departmentCode: PropTypes.string,
  displayTooltipInPortal: PropTypes.bool,
  empty: PropTypes.bool,
  expanded: PropTypes.bool,
  firstInGroup: PropTypes.bool,
  flyoutInformations: PropTypes.shape({}),
  isDraft: PropTypes.bool,
  isInGroup: PropTypes.bool,
  isPaused: PropTypes.bool,
  isStarted: PropTypes.bool,
  isStatusCompleted: PropTypes.bool,
  lastInGroup: PropTypes.bool,
  leftOffset: PropTypes.number,
  lengthMultiplier: PropTypes.number,
  lifeStatus: PropTypes.oneOf(['', ...singleTaskLifeStatuses]),
  onTaskClick: PropTypes.func,
  people: PropTypes.shape({}),
  readyStatus: PropTypes.shape({}),
  rightOffset: PropTypes.number,
  scheduleType: PropTypes.string,
  showTiming: PropTypes.bool,
  taskCommentCount: PropTypes.number,
  taskCompany: PropTypes.shape({}),
  taskDraftId: PropTypes.number,
  taskHome: PropTypes.shape({}),
  taskId: PropTypes.number,
  taskIssueCount: PropTypes.number,
  taskName: PropTypes.string,
  taskSeries: PropTypes.number,
  taskStatus: PropTypes.shape({}),
  tooltipSettings: PropTypes.shape({
    containerWidth: PropTypes.number,
    paddingHorizontalRemoval: PropTypes.number,
  }),
  totalTime: PropTypes.string,
  withText: PropTypes.bool,
  withToolTip: PropTypes.bool,
};

SingleTask.defaultProps = {
  assignedPeople: [],
  clickable: true,
  containerClassName: '',
  departmentCode: DEPARTMENTS.HOUSEKEEPING,
  empty: false,
  expanded: false,
  firstInGroup: false,
  isDraft: false,
  isInGroup: false,
  isPaused: false,
  isStarted: false,
  isStatusCompleted: false,
  lastInGroup: false,
  leftOffset: 0,
  lengthMultiplier: 1,
  lifeStatus: '',
  onTaskClick: () => {},
  people: {},
  readyStatus: {},
  rightOffset: 0,
  scheduleType: null,
  showTiming: false,
  taskCommentCount: 0,
  taskDraftId: null,
  taskHome: null,
  taskId: null,
  taskIssueCount: 0,
  taskName: '',
  taskSeries: null,
  taskStatus: {},
  tooltipSettings: {},
  totalTime: null,
  withText: false,
  withToolTip: false,
};

export default SingleTask;
